import { useCallback, useEffect, useState } from 'react'
import { Loader2, Plus, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@orisini/ui/components/button'
import { Card, CardContent } from '@orisini/ui/components/card'
import { Input } from '@orisini/ui/components/input'
import { api, ApiError } from '@orisini/ui/lib/api'
import { accountsApi, type AccountDto } from '@/features/accounts/api'

interface BoardDto {
  id: string
  name: string
  privacy: string | null
  imageUrl: string | null
  pinCount: number | null
  manual: boolean
  archived: boolean
}

export default function BoardsPage() {
  const [accounts, setAccounts] = useState<AccountDto[]>([])
  const [accountId, setAccountId] = useState('')
  const [boards, setBoards] = useState<BoardDto[]>([])
  const [lastSyncAt, setLastSyncAt] = useState<number | null>(null)
  const [syncing, setSyncing] = useState(false)
  const [newName, setNewName] = useState('')

  useEffect(() => {
    accountsApi
      .list()
      .then((res) => {
        setAccounts(res.accounts)
        if (res.accounts.length) setAccountId(res.accounts[0].id)
      })
      .catch(() => undefined)
  }, [])

  const load = useCallback(async (accId: string) => {
    const [boardsRes, statusRes] = await Promise.all([
      api.get<{ boards: BoardDto[] }>(`/accounts/${accId}/boards`),
      api.get<{ lastSyncAt: number | null }>(`/accounts/${accId}/boards/sync-status`),
    ])
    setBoards(boardsRes.boards)
    setLastSyncAt(statusRes.lastSyncAt)
  }, [])

  useEffect(() => {
    if (accountId) void load(accountId).catch(() => undefined)
  }, [accountId, load])

  async function sync() {
    setSyncing(true)
    try {
      await api.post(`/accounts/${accountId}/boards/sync`)
      toast.success('Sync requested — the agent is fetching the board list')
      // Poll sebentar sampai daftar berubah.
      for (let i = 0; i < 10; i++) {
        await new Promise((r) => setTimeout(r, 2000))
        await load(accountId)
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Couldn\'t request sync')
    } finally {
      setSyncing(false)
    }
  }

  async function addManual() {
    if (!newName.trim()) return
    try {
      await api.post(`/accounts/${accountId}/boards`, { name: newName.trim() })
      setNewName('')
      await load(accountId)
      toast.success('Manual board added')
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Couldn\'t add board')
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Boards</h1>
          <p className="text-sm text-muted-foreground">
            {boards.length} boards
            {lastSyncAt && ` · last sync ${new Date(lastSyncAt).toLocaleString('en-GB')}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {accounts.length > 1 && (
            <select
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
            >
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.label}
                </option>
              ))}
            </select>
          )}
          <Button onClick={() => void sync()} disabled={syncing || !accountId}>
            {syncing ? <Loader2 className="animate-spin" /> : <RefreshCw />}
            Sync now
          </Button>
        </div>
      </header>

      <Card>
        <CardContent className="flex flex-wrap items-center gap-2 p-4">
          <Input
            className="max-w-xs"
            placeholder="Add a manual board (name exactly as on Pinterest)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <Button variant="outline" onClick={() => void addManual()} disabled={!newName.trim()}>
            <Plus />
            Add
          </Button>
          <p className="w-full text-xs text-muted-foreground">
            Manual entries help when sync isn't running yet. The name must match exactly — the agent
            matches boards by name when posting.
          </p>
        </CardContent>
      </Card>

      {boards.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No boards yet. Sync from the agent, or add one manually.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {boards.map((b) => (
            <Card key={b.id}>
              <CardContent className="flex items-center gap-3 p-3">
                <div className="size-12 shrink-0 overflow-hidden rounded bg-muted">
                  {b.imageUrl && <img src={b.imageUrl} alt="" className="size-full object-cover" loading="lazy" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{b.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {b.pinCount !== null ? `${b.pinCount} pins` : '—'}
                    {b.privacy === 'secret' && ' · secret'}
                    {b.manual && ' · manual'}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
